import { PopoverTrigger } from "@/components/ui/popover";
import type { MetadataIcon } from "@cohesive/api-client";
import { Icon as MetadataIconRenderer } from "@cohesive/ui-components";
import { Layers } from "lucide-react";
import { SwitcherCaret, WorkspaceSwitcherPopover } from "./WorkspaceSwitcher";

/**
 * "in <icon> <Workspace>" chip, rendered in the composer toolbar just right of
 * the skills button. Sending from a workspace page starts the chat in that
 * workspace, which is otherwise invisible at the point of typing. It sits with
 * the other toolbar controls rather than above the editor so the input's
 * geometry stays identical between `/` and a workspace.
 *
 * It's also a secondary entry point to the workspace switcher — the "wait,
 * where will this chat go?" moment — opening the same popover as the header
 * title/greeting, with the same navigate-on-select behavior. No Home entry
 * here: the chip is about which workspace a chat lands in, and Home isn't a
 * chat target — on `/` the checked row is My Workspace, where unscoped chats
 * actually go.
 */
export function ComposerScopeChip({
  name,
  icon,
  currentWorkspaceId,
}: {
  name: string;
  icon?: MetadataIcon | null;
  /** Workspace page the composer sits on; undefined on the unscoped home. */
  currentWorkspaceId?: string;
}) {
  return (
    <WorkspaceSwitcherPopover
      currentWorkspaceId={currentWorkspaceId}
      includeHome={false}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Switch workspace"
          className="group -mx-1 flex min-w-0 items-center gap-1.5 rounded-md px-1 py-0.5 text-xs text-muted-foreground select-none hover:bg-muted/60 data-[state=open]:bg-muted/60"
        >
          <span>in</span>
          <span className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center">
            {icon ? (
              <MetadataIconRenderer icon={icon} size={14} />
            ) : (
              <Layers className="h-3.5 w-3.5" />
            )}
          </span>
          <span className="truncate font-medium text-foreground/80">
            {name}
          </span>
          <SwitcherCaret className="h-3 w-3" />
        </button>
      </PopoverTrigger>
    </WorkspaceSwitcherPopover>
  );
}
